/**
 * Empty State Component
 * 
 * Friendly empty state display with illustration
 * 
 * Version: 6.2.0
 */
import React, { ReactNode } from 'react'
import './EmptyState.css'

interface EmptyStateProps {
  title: string
  description?: string
  icon?: ReactNode
  illustration?: 'strategy' | 'broker' | 'trade' | 'analytics' | 'notification' | 'search' | 'error' | 'custom'
  action?: {
    label: string
    onClick: () => void
    icon?: ReactNode
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  children?: ReactNode
  className?: string
}

const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  illustration = 'strategy',
  action,
  secondaryAction,
  children,
  className = ''
}) => {
  // SVG Illustrations
  const illustrations: Record<string, ReactNode> = {
    strategy: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="30" width="140" height="100" rx="12" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="2"/>
        <path d="M50 80 L80 60 L110 75 L150 45" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="50" cy="80" r="5" fill="var(--primary)"/>
        <circle cx="80" cy="60" r="5" fill="var(--primary)"/>
        <circle cx="110" cy="75" r="5" fill="var(--primary)"/>
        <circle cx="150" cy="45" r="5" fill="var(--success)"/>
        <path d="M50 95 L150 95" stroke="var(--border-color)" strokeWidth="1" strokeDasharray="4 4"/>
        <rect x="70" y="110" width="60" height="8" rx="4" fill="var(--bg-tertiary)"/>
      </svg>
    ),
    broker: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="60" r="35" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="2"/>
        <path d="M85 55 L95 65 L115 45" stroke="var(--success)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="50" y="110" width="100" height="30" rx="8" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="2"/>
        <circle cx="75" cy="125" r="6" fill="var(--primary)"/>
        <circle cx="100" cy="125" r="6" fill="var(--success)"/>
        <circle cx="125" cy="125" r="6" fill="var(--warning)"/>
        <rect x="30" y="25" width="20" height="20" rx="4" fill="var(--bg-tertiary)"/>
        <rect x="150" y="25" width="20" height="20" rx="4" fill="var(--bg-tertiary)"/>
      </svg>
    ),
    trade: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="40" y="40" width="120" height="80" rx="8" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="2"/>
        <path d="M55 95 L75 75 L95 85 L145 55" stroke="var(--success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M55 105 L145 105" stroke="var(--border-color)" strokeWidth="1"/>
        <path d="M55 50 L55 110" stroke="var(--border-color)" strokeWidth="1"/>
        <circle cx="145" cy="55" r="6" fill="var(--success)"/>
        <rect x="160" y="30" width="15" height="15" rx="3" fill="var(--success)"/>
        <rect x="160" y="115" width="15" height="15" rx="3" fill="var(--danger)"/>
      </svg>
    ),
    analytics: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="50" width="30" height="70" rx="4" fill="var(--primary)" opacity="0.3"/>
        <rect x="30" y="80" width="30" height="40" rx="4" fill="var(--primary)"/>
        <rect x="75" y="65" width="30" height="55" rx="4" fill="var(--success)" opacity="0.3"/>
        <rect x="75" y="90" width="30" height="30" rx="4" fill="var(--success)"/>
        <rect x="120" y="45" width="30" height="75" rx="4" fill="var(--warning)" opacity="0.3"/>
        <rect x="120" y="70" width="30" height="50" rx="4" fill="var(--warning)"/>
        <path d="M30 35 L170 35" stroke="var(--border-color)" strokeWidth="2"/>
        <circle cx="170" cy="35" r="5" fill="var(--primary)"/>
      </svg>
    ),
    notification: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M100 30 C60 30 35 60 35 95 C35 115 45 130 60 140 L60 150 L140 150 L140 140 C155 130 165 115 165 95 C165 60 140 30 100 30Z" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="2"/>
        <path d="M80 60 L100 80 L120 60" stroke="var(--primary)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="155" cy="45" r="15" fill="var(--danger)"/>
        <path d="M150 45 L160 45 M155 40 L155 50" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    search: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="85" cy="75" r="40" fill="var(--bg-secondary)" stroke="var(--border-color)" strokeWidth="3"/>
        <path d="M115 105 L155 145" stroke="var(--primary)" strokeWidth="6" strokeLinecap="round"/>
        <circle cx="75" cy="65" r="15" fill="var(--bg-tertiary)"/>
        <circle cx="95" cy="85" r="10" fill="var(--bg-tertiary)"/>
      </svg>
    ),
    error: (
      <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="80" r="50" fill="var(--bg-secondary)" stroke="var(--danger)" strokeWidth="2"/>
        <path d="M75 60 L125 100" stroke="var(--danger)" strokeWidth="4" strokeLinecap="round"/>
        <path d="M125 60 L75 100" stroke="var(--danger)" strokeWidth="4" strokeLinecap="round"/>
        <circle cx="45" cy="40" r="8" fill="var(--danger)" opacity="0.3"/>
        <circle cx="155" cy="120" r="12" fill="var(--danger)" opacity="0.3"/>
      </svg>
    ),
    custom: null
  }

  return (
    <div className={`empty-state ${className}`}>
      {/* Illustration */}
      <div className="empty-state-illustration">
        {icon || illustrations[illustration]}
      </div>

      {/* Content */}
      <div className="empty-state-content">
        <h3 className="empty-state-title">{title}</h3>
        {description && (
          <p className="empty-state-description">{description}</p>
        )}

        {/* Children (custom content) */}
        {children}

        {/* Actions */}
        {(action || secondaryAction) && (
          <div className="empty-state-actions">
            {action && (
              <button className="empty-state-action primary" onClick={action.onClick}>
                {action.icon && <span className="action-icon">{action.icon}</span>}
                {action.label}
              </button>
            )}
            {secondaryAction && (
              <button className="empty-state-action secondary" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default EmptyState